import React, { useEffect, useRef, useState } from "react";  // ← added useEffect
import * as Fa from "react-icons/fa";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import { GetDateFormat } from "../../../Services/GlobalFunction/GetDateFormat";
import { downloadExcel } from "../../../Services/GlobalFunction/DownLoadExcel";
import { AxiosExcel } from "../../../API/Axios";
import { fetchData } from "../../../Services/GlobalService";
import Loader from "../../../Components/Loader";


const currentYear = new Date().getFullYear();

const FORMAT_LIST = [
  { label: "xlsx", value: "xlsx" },
  { label: "csv",  value: "csv"  },
];

const SIPWeekDefinition: React.FC = () => {
  const UserId    = localStorage.getItem("UserId")?.toString();
  const getformat = GetDateFormat();
  const CommonDropDownUrl = "CommonDropdown";
  const BaseUrl = "SIPWeekDefinition";
  const Endpoint = {
    DownloadWeekDefinition: "DownloadWeekDefinition",
    UploadFile:             "UploadWeekDefinationData",
    DropdownList:           "DropDownList"
  };

  const fileUploadRef = useRef<FileUpload>(null);

  /* ── State ── */
  const [yearOptions,           setYearOptions]           = useState<{ label: string; value: any }[]>([]);  
  const [selectedYear,          setSelectedYear]          = useState<any>(null);
  const [loading,               setLoading]               = useState<boolean>(false);
  const [templateFormat,        setTemplateFormat]        = useState<string>("xlsx");

  /* ── Fetch Year Dropdown from API ── */
  useEffect(() => {
    const fetchYearOptions = async () => {
      try {
        const res = await fetchData(
          CommonDropDownUrl,
          Endpoint.DropdownList,
          `UserId=${UserId}&PVCtype=DBSIP_year`
        );

        if (res?.Data?.length > 0) {
          const options = res.Data.map((item: any) => ({
            label: String(item.Year_Value),
            value: item.Year_Value,
          }));
          setYearOptions(options);

          const defaultYear =
            options.find(
              (y: { value: any }) => Number(y.value) === currentYear,
            ) ?? options[options.length - 1];
          setSelectedYear(defaultYear?.value ?? null);
        }
      } catch (ex: any) {
        toast.error(ex?.response?.data?.Message ?? "Failed to load year options");
      }
    };

    fetchYearOptions();
  }, []);

  /* ── Download Template — client-side, headers only ── */
  const handleDownloadTemplate = () => {
    const headers = [{ "Quarter": null, "Week": null, "Start Date": null, "End Date": null }];
    downloadExcel(headers, `SIP_Week_Definition_Template_${getformat}.${templateFormat}`);
  };

  const handleDownloadWeekDefinition = async () => {
    setLoading(true);
    try {
      const res = await fetchData(
        BaseUrl,
        Endpoint.DownloadWeekDefinition,
        `Year=${selectedYear}`
      );
      if (res?.Data?.length > 0) {
        downloadExcel(res.Data, `SIP_Week_Definition_${getformat}.xlsx`);
      } else {
        toast.warn("No data found.");
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to download week definition");
    } finally {
      setLoading(false);
    }
  };

  /* ── File Upload ── */
  const handleFileUpload = async (event: any) => {
    setLoading(true);
    try {
      const file = event.files[0];
      const formData = new FormData();
      formData.append("FileContent", file);
      formData.append("Year",        String(selectedYear));
      formData.append("UserId",      UserId ?? "");

      const response = await AxiosExcel.post(
        `${BaseUrl}/${Endpoint.UploadFile}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const res = response.data;
      if (res.Status === "Success") {
        toast.success(res.Message);
        fileUploadRef.current?.clear();
      } else if (res.Status === "ErrorFR") {
        toast.warn(res.message ?? res.Message);
        fileUploadRef.current?.clear();
      } else if (res.Status === "Error") {
        toast.warn(res.message ?? res.Message);
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.message ?? "Upload failed");
      fileUploadRef.current?.clear();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <main>
      <div className="col-12 mb-5">

        {/* ── Breadcrumb ── */}
        <div className="col-lg-12 col-md-12 col-sm-12">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mx-0">
              <li className="breadcrumb-item">SIP</li>
              <li className="breadcrumb-item active" aria-current="page">
                SIP Week Definition
              </li>
            </ol>
          </nav>
        </div>

        {/* ── Page title + action buttons ── */}
        <div className="row">
          <div className="col-lg-4 col-md-12 col-sm-12">
            <h3 className="py-2">SIP Week Definition</h3>
          </div>
          <div className="col-lg-8 col-md-12 col-sm-12">
            <div className="text-end">
              <div className="d-inline-flex">
                <Dropdown
                  value={templateFormat}
                  onChange={(e) => setTemplateFormat(e.value)}
                  options={FORMAT_LIST}
                  optionLabel="label"
                  style={{
                    width: 100,
                    borderRadius: "0 6px 6px 0",
                    border: "1px solid #ced4da",
                    borderLeft: "none",
                    height: "100%",
                  }}
                />
              </div>
              <button
                type="button"
                className="btn anime-secondary-btn mx-2"
                onClick={handleDownloadTemplate}
              >
                Download Template <Fa.FaDownload className="ms-2" />
              </button>
              <button
                type="button"
                className="btn anime-btn"
                onClick={handleDownloadWeekDefinition}
                disabled={loading}
              >
                {loading ? (
                  <>
                    Exporting...{" "}
                    <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true" />
                  </>
                ) : (
                  <>Export <Fa.FaFileExport className="ms-2" /></>
                )}
              </button>
            </div>
          </div>
        </div>

        <section className="sales-box">
          <div className="row d-flex justify-content-end align-items-end">
            <div className="col-lg-7 col-md-7 col-sm-12">
              <div className="row">

                {/* DB Year dropdown — now API-driven */}
                <div className="col-lg-6 col-md-12 col-sm-12 mb-3">
                  <div className="d-block">
                    <label className="form-label">DBSIP Year</label>
                    <Dropdown
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.value)}
                      options={yearOptions}
                      optionLabel="label"
                      placeholder="Select Year"
                      className="w-100"
                    />
                  </div>
                </div>

                {/* Upload section */}
                <div className="col-lg-12 col-md-12 col-sm-12 mt-2">
                  <div className="UploadSection clearfix">
                    <div className="conText">
                      <Fa.FaInfoCircle className="icon" /> Select an Excel or CSV File to upload
                    </div>
                    <FileUpload
                      ref={fileUploadRef}
                      className="clearfix nTopMargin"
                      name="demo[]"
                      accept=".xlsx, .xls, .csv"
                      emptyTemplate={
                        <p className="m-0">Drag and drop files to here to upload.</p>
                      }
                      customUpload
                      uploadHandler={handleFileUpload}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-5 col-md-5 col-sm-12">
              <div className="text-end">
                <img
                  src={require("../../../Assets/Images/File_Upload.png")}
                  className="File_Upload_Img drop-right"
                  alt="InnoRise"
                />
              </div>
            </div>
          </div>
            <small className="text-success my-2">
              <i>
                <b>*Note:</b> Start date and End date should be in <b>DD/MM/YYYY</b> format
              </i>
            </small>
        </section>
      </div>
    </main>
  );
};

export default SIPWeekDefinition;